<template>
    <Page withNavbar>
        <main :class="classes.main">
            <section class="infos">
                <div class="image">
                    <img :src="user.picture"/>
                </div>
                <div class="general">
                    <h4 :style="configStyles.primary.text">
                        {{ config.PROFILE_INFO_GENERAL_TITLE }}
                    </h4>
                    <form>
                        <Input
                            v-model="form.user.lastName"
                            type="text"
                            :label="config.PROFILE_INFO_GENERAL_SURNAME"
                            :placeholder="config.PROFILE_INFO_GENERAL_SURNAME"
                        />
                        <Input
                            v-model="form.user.firstName"
                            type="text"
                            :label="config.PROFILE_INFO_GENERAL_FIRSTNAME"
                            :placeholder="config.PROFILE_INFO_GENERAL_FIRSTNAME"
                        />
                        <Input
                            v-model="form.user.birthdate"
                            type="date"
                            :label="config.PROFILE_INFO_GENERAL_BIRTHDATE"
                            :placeholder="config.PROFILE_INFO_GENERAL_BIRTHDATE"
                        />
                        <Input
                            v-model="form.user.email"
                            type="text"
                            :label="config.PROFILE_INFO_GENERAL_EMAIL"
                            :placeholder="config.PROFILE_INFO_GENERAL_EMAIL"
                        />
                        <InputPhone
                            v-model="form.user.phone"
                            :label="config.PROFILE_INFO_GENERAL_PHONE"
                            :placeholder="config.PROFILE_INFO_GENERAL_PHONE"
                        />
                        <div v-show="user.allowedOtherCampaigns" class="campaigns-checkbox">
                            <label>{{ config.PROFILE_INFO_GENERAL_CAMPAIGN }}</label>
                            <Checkbox v-model="form.user.campaignsOption">
                                {{ config.PROFILE_INFO_GENERAL_CAMPAIGN_LABEL }}
                            </Checkbox>
                        </div>
                    </form>
                </div>
                <div v-if="config.SHOW_PREFERENCES_PROFILE === 'true'" class="preferences">
                    <h4 :style="configStyles.primary.text">
                        {{ config.PREFERENCES_TITLE }}
                    </h4>
                    <p class="preferences-subtitle">
                        {{ config.PREFERENCES_SUBTITLE }}
                    </p>
                    <div class="preferences-container">
                        <span
                            v-for="preference in preferences"
                            :key="preference.id"
                            :value="preference.name"
                            :class="isPreferenceSelected(preference)"
                            class="preferences-options"
                            @click="togglePreference(preference)"
                        >
                            {{ config[preference.name] }}
                        </span>
                    </div>
                </div>
                <div class="addresses">
                    <h4 :style="configStyles.primary.text">
                        {{ config.PROFILE_ADDRESSES_TITLE }}
                    </h4>
                    <AddressItem
                        v-for="address in user.addresses"
                        :key="address.id"
                        :address="address"
                        @onEdit="onAddressEdit"
                        @onDelete="onAddressDelete"
                    />
                    <p v-if="!user.addresses.length">
                        {{ config.PROFILE_ADDRESSES_EMPTY }}
                    </p>
                    <Button type="button" class="button" :color="config.COLOR_PRIMARY" @click="onAddressCreate">
                        {{ config.PROFILE_CREATE_ADDRESS }}
                    </Button>
                </div>
                <div class="social">
                    <h4 :style="configStyles.primary.text">
                        {{ config.PROFILE_INFO_SOCIAL_TITLE }}
                    </h4>
                    <form>
                        <Input
                            v-model="form.user.social.instagram"
                            type="text"
                            :label="config.PROFILE_INFO_SOCIAL_INSTAGRAM"
                            :placeholder="config.PROFILE_INFO_SOCIAL_INSTAGRAM"
                            :error="config.ERROR_BAD_LINK"
                            regexValidation="INSTAGRAM"
                        />
                        <Input
                            v-model="form.user.social.youtube"
                            type="text"
                            :label="config.PROFILE_INFO_SOCIAL_YOUTUBE"
                            :placeholder="config.PROFILE_INFO_SOCIAL_YOUTUBE"
                            :error="config.ERROR_BAD_LINK"
                            regexValidation="YOUTUBE"
                        />
                        <Input
                            v-model="form.user.social.twitter"
                            type="text"
                            :label="config.PROFILE_INFO_SOCIAL_TWITTER"
                            :placeholder="config.PROFILE_INFO_SOCIAL_TWITTER"
                            :error="config.ERROR_BAD_LINK"
                            regexValidation="TWITTER"
                        />
                        <Input
                            v-model="form.user.social.snapchat"
                            type="text"
                            :label="config.PROFILE_INFO_SOCIAL_SNAPCHAT"
                            :placeholder="config.PROFILE_INFO_SOCIAL_SNAPCHAT"
                            :error="config.ERROR_BAD_LINK"
                            regexValidation="SNAPCHAT"
                        />
                        <Input
                            v-model="form.user.social.website"
                            type="text"
                            :label="config.PROFILE_INFO_SOCIAL_BLOG"
                            :placeholder="config.PROFILE_INFO_SOCIAL_BLOG"
                        />
                    </form>
                </div>
            </section>
            <section v-if="showUserVideos" class="videos">
                <h4 :style="configStyles.primary.text">
                    {{ config.PROFILE_VIDEOS_TITLE }} ({{ validatedProjects.length }})
                </h4>
                <ProjectsList :projects="validatedProjects" @click="onProjectClick"/>
            </section>
            <section :class="classes.privacy">
                <div class="trigger" @click="onPrivacy">
                    <h4 :style="configStyles.primary.text">
                        {{ config.PROFILE_PRIVACY_TITLE }}
                    </h4>
                    <ChevronDown :color="config.COLOR_PRIMARY"/>
                </div>
                <TransitionCollapse>
                    <div v-show="privacy" class="content">
                        <template v-if="this.user.canDeleteAccount">
                            <p>
                                {{ config.PROFILE_EDIT_DELETE }}
                            </p>
                            <p>
                                <a :style="configStyles.primary.text" @click="onDelete">
                                    {{ config.PROFILE_EDIT_DELETE_CTA }}
                                </a>
                            </p>
                        </template>
                        <p v-if="!this.user.canDeleteAccount" class="warning-delete">
                            {{ config.PROFILE_EDIT_CANT_DELETE }}
                        </p>
                    </div>
                </TransitionCollapse>
            </section>
            <section class="actions">
                <Button :color="config.COLOR_PRIMARY" :disabled="loading" outline @click="onCancel">
                    {{ config.PROFILE_EDIT_CANCEL }}
                </Button>
                <Button :color="config.COLOR_PRIMARY" :disabled="loading" @click="onSubmit">
                    {{ config.PROFILE_EDIT_SAVE }}
                </Button>
            </section>
        </main>
        <!-- Modals -->
        <transition name="fade">
            <ModalProfileDelete
                v-if="modals.delete"
                @close="onDeleteClose"
                @done="onDeleteDone"
            />
            <ModalProject
                v-if="modals.project"
                :project="modals.project"
                @close="onProjectClose"
            />
            <ModalAddress
                v-if="modals.address"
                :address="form.address"
                @close="onAddressClose"
            />
            <ModalAddressDelete
                v-if="modals.deleteAddress"
                :address="form.address"
                @close="onAddressDeleteClose"
            />
        </transition>
    </Page>
</template>
<script>
import { SolutionConsumer, UserConsumer, ProjectConsumer } from '../../mixins';
import {
    Page, ModalProfileDelete, ModalProject, ModalAddress,
} from '../../components/desktop';
import { ProjectsList, ModalAddressDelete, AddressItem } from '../../components/shared';
import { regexBySocialNetwork } from '../../utils';
import Checkbox from '../../components/lib/Checkbox';

export default {
    name: 'Profile',
    components: {
        Checkbox,
        ModalAddressDelete,
        ModalAddress,
        Page,
        ModalProfileDelete,
        ModalProject,
        ProjectsList,
        AddressItem,
    },
    mixins: [SolutionConsumer, UserConsumer, ProjectConsumer],
    data () {
        return {
            loading: false,
            form: {
                user: {},
                address: {},
            },
            modals: {
                delete: false,
                project: null,
                address: false,
                deleteAddress: false,
            },
            privacy: false,
            preferences: [],
        };
    },
    computed: {
        classes () {
            return {
                main: {
                    'with-videos': this.showUserVideos,
                    'without-videos': !this.showUserVideos,
                },
                privacy: {
                    privacy: true,
                    open: this.privacy,
                },
            };
        },
        isPreferenceSelected () {
            return (pref) => ({
                selected: this.form.user.preferences.includes(pref.preferenceId),
            });
        },
    },
    async created () {
        // Initialize form from user information
        if (this.config.SHOW_PREFERENCES_PROFILE === 'true') {
            this.preferences = await this.getAllReferences();
        }
        this.form.user = {
            firstName: this.user.firstName,
            lastName: this.user.lastName,
            email: this.user.email,
            phone: this.user.phone,
            birthdate: this.user.birthdate,
            social: {
                twitter: this.user.social.twitter,
                instagram: this.user.social.instagram,
                youtube: this.user.social.youtube,
                snapchat: this.user.social.snapchat,
                facebook: this.user.social.facebook,
                website: this.user.social.website,
            },
            preferences: this.user.preferences,
            campaignsOption: this.user.campaignsOption,
        };
    },
    methods: {
        onPrivacy () {
            this.privacy = !this.privacy;
        },
        onCancel () {
            this.$router.go(-1);
        },
        async onSubmit () {
            this.loading = true;

            if ((this.form.user.social.instagram && !regexBySocialNetwork.INSTAGRAM.test(this.form.user.social.instagram))
                || (this.form.user.social.youtube && !regexBySocialNetwork.YOUTUBE.test(this.form.user.social.youtube))
                || (this.form.user.social.snapchat && !regexBySocialNetwork.SNAPCHAT.test(this.form.user.social.snapchat))
                || (this.form.user.social.twitter && !regexBySocialNetwork.TWITTER.test(this.form.user.social.twitter))
                || (this.form.user.social.facebook && !regexBySocialNetwork.FACEBOOK.test(this.form.user.social.facebook))
            ) {
                this.loading = false;
            } else {
                try {
                    // Update user info
                    await this.editUser({
                        ...this.form.user,
                        campaignsOption:
                            // if value was not updated - send null because we do not need a log for this.
                            this.user.campaignsOption === this.form.user.campaignsOption
                                ? null
                                : this.form.user.campaignsOption,
                    });
                    // Update local copy
                    await this.updateUser();
                } catch (err) {
                    console.error(err);
                } finally {
                    this.loading = false;
                }
            }
        },
        onDelete () {
            this.modals.delete = true;
        },
        onDeleteClose () {
            this.modals.delete = false;
        },
        async onDeleteDone () {
            try {
                this.modals.delete = false;
            } catch (err) {
                console.log(err);
            }
        },
        onProjectClose () {
            this.modals.project = null;
        },
        onProjectClick (project) {
            this.modals.project = project;
        },
        onAddressDelete (address) {
            this.modals.deleteAddress = true;
            this.form.address = address;
        },
        onAddressDeleteClose () {
            this.modals.deleteAddress = false;
            this.form.address = {};
        },
        onAddressCreate () {
            this.modals.address = true;
        },
        onAddressEdit (address = {}) {
            this.modals.address = true;
            this.form.address = address;
        },
        onAddressClose () {
            this.modals.address = false;
            this.form.address = {};
        },
        togglePreference (pref) {
            const existing = this.form.user.preferences.filter((preferenceId) => preferenceId === pref.preferenceId);
            if (existing.length) {
                this.form.user.preferences = this.form.user.preferences
                    .filter((preferenceId) => preferenceId !== pref.preferenceId);
            } else {
                this.form.user.preferences = [...this.form.user.preferences, pref.preferenceId];
            }
        },
    },
};
</script>
<style lang="scss" scoped>
@import '../../assets/scss/mixins';
@import '../../assets/scss/colors';

main {
  @include container(20px);
  margin: 0 auto;

  > section {
    &.infos {
      @include card(40px);
      grid-area: infos;
      display: grid;
      gap: 40px;

      > .image {
        justify-self: center;

        > img {
          @include profile-picture();
          @include square(100px);
        }
      }

      > .general, > .social {
        > form {
          display: grid;
          gap: 20px 60px;
        }
      }

      > .addresses {
        > .button {
          margin-top: 15px;
        }
      }

      .campaigns-checkbox {
        > label:first-child {
          display: block;
          padding: 8px 0;
          font-size: 12px;
          font-weight: 400;
          color: $black;
        }
      }
    }

    &.videos {
      @include card(20px);
      grid-area: videos;
    }

    &.privacy {
      @include card(20px);
      grid-area: privacy;
      padding: 0 20px;

      > .trigger {
        display: flex;
        justify-content: space-between;
        align-items: center;
        cursor: pointer;

        > h4 {
          margin: 14px 0;
        }

        > svg {
          transition: transform .2s ease-in-out;
        }
      }

      > .content {
        padding-bottom: 20px;

        > p:nth-child(2) {
          text-align: right;
          font-size: 12px;
          font-weight: bold;
          cursor: pointer;
        }
      }

      &.open {
        > .trigger {
          > svg {
            transform: rotate(180deg);
          }
        }
      }
    }

    &.actions {
      grid-area: actions;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 40px 0;

      button {
        margin: 0 20px;
      }
    }
  }

  .preferences-subtitle {
    margin-bottom: 1rem;
  }

  .preferences-container {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    column-gap: 1.5rem;
  }

  .preferences-options {
    border-radius: 1rem;
    background-color: rgb(231, 231, 231);
    color: rgb(136, 133, 133);
    margin-bottom: 1rem;
    padding: 0.5rem 1rem;
  }

  .preferences-options.selected {
    background-color: rgb(30, 165, 124);
    color: white;
  }

  .preferences-options.selected::before {
    background-image: url('../../assets/img/check-white.svg');
    background-repeat: no-repeat;
    content: '';
    display: inline-block;
    height: 0.7rem;
    width: 0.8rem;
  }

  .preferences-options:hover {
    cursor: pointer;
  }

  @include breakpoint-min(xs) {
    width: 100%;

    &.with-videos {
      grid-template-areas:
                'infos'
                'videos'
                'privacy'
                'actions';
    }

    &.without-videos {
      grid-template-areas:
                'infos'
                'privacy'
                'actions';
    }
  }

  @include breakpoint-min(sm) {
    width: 70%;
  }

  @include breakpoint-min(md) {
    &.with-videos {
      width: 100%;
      grid-template-columns: 1fr 1fr;
      grid-template-areas:
                'infos videos'
                'privacy videos'
                'actions actions';
    }
  }

  @include breakpoint-min(lg) {
    &.with-videos {
      width: 80%;
    }

    > section {
      &.infos {
        > .general, > .social {
          > form {
            grid-template-columns: repeat(2, 1fr);
            grid-auto-rows: 1fr;
          }
        }
      }
    }
  }

  @include breakpoint-min(xl) {
    &.with-videos {
      grid-template-columns: 2fr 1fr;
    }
  }
}
</style>
